import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/lib/auth';
import type { User } from '@/types';
import { Instagram } from 'lucide-react';

const schema = z.object({
  full_name: z.string().min(2, 'Name must be at least 2 characters'),
  bio: z.string().min(50, 'Bio must be at least 50 characters').optional(),
});

type ProfileFormData = z.infer<typeof schema>;

interface ProfileDetailsProps {
  user: User;
}

export function ProfileDetails({ user }: ProfileDetailsProps) {
  const { completeProfile } = useAuth();
  const [isUpdating, setIsUpdating] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState(false);
  const [connectingInstagram, setConnectingInstagram] = React.useState(false);
  const [referral1, setReferral1] = React.useState('');
  const [referral2, setReferral2] = React.useState('');
  const [countryCode, setCountryCode] = useState('+1');
  const [phone, setPhone] = useState(user.phone_number || '');

  const { register, handleSubmit, formState: { errors, isDirty } } = useForm<ProfileFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      full_name: user.full_name,
      bio: user.bio,
    },
  });

  const onSubmit = async (data: ProfileFormData) => {
    try {
      setIsUpdating(true);
      setError(null);
      
      // Preserve existing user data while updating profile
      await completeProfile({
        ...data,
        role: user.role, // Preserve existing role
      });
      
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Failed to update profile:', error);
      setError(error instanceof Error ? error.message : 'Failed to update profile');
    } finally {
      setIsUpdating(false);
    }
  };

  // Placeholder for Instagram connect logic
  const handleConnectInstagram = async () => {
    setConnectingInstagram(true);
    // TODO: Replace with real OAuth flow
    setTimeout(() => {
      alert('Instagram connect coming soon!');
      setConnectingInstagram(false);
    }, 1000);
  };

  const countryCodes = [
    { code: '+1', label: '🇺🇸 United States (+1)' },
    { code: '+44', label: '🇬🇧 United Kingdom (+44)' },
    { code: '+61', label: '🇦🇺 Australia (+61)' },
    { code: '+91', label: '🇮🇳 India (+91)' },
    { code: '+33', label: '🇫🇷 France (+33)' },
    { code: '+49', label: '🇩🇪 Germany (+49)' },
    { code: '+81', label: '🇯🇵 Japan (+81)' },
    { code: '+39', label: '🇮🇹 Italy (+39)' },
    { code: '+34', label: '🇪🇸 Spain (+34)' },
    { code: '+41', label: '🇨🇭 Switzerland (+41)' },
    { code: '+971', label: '🇦🇪 UAE (+971)' },
    { code: '+852', label: '🇭🇰 Hong Kong (+852)' },
    { code: '+65', label: '🇸🇬 Singapore (+65)' },
    { code: '+86', label: '🇨🇳 China (+86)' },
    { code: '+7', label: '🇷🇺 Russia (+7)' },
    { code: '+351', label: '🇵🇹 Portugal (+351)' },
    { code: '+46', label: '🇸🇪 Sweden (+46)' },
    { code: '+31', label: '🇳🇱 Netherlands (+31)' },
    { code: '+32', label: '🇧🇪 Belgium (+32)' },
    { code: '+358', label: '🇫🇮 Finland (+358)' },
    { code: '+47', label: '🇳🇴 Norway (+47)' },
    { code: '+45', label: '🇩🇰 Denmark (+45)' },
    { code: '+353', label: '🇮🇪 Ireland (+353)' },
    { code: '+43', label: '🇦🇹 Austria (+43)' },
    { code: '+420', label: '🇨🇿 Czechia (+420)' },
    { code: '+48', label: '🇵🇱 Poland (+48)' },
    { code: '+90', label: '🇹🇷 Turkey (+90)' },
    { code: '+30', label: '🇬🇷 Greece (+30)' },
    { code: '+52', label: '🇲🇽 Mexico (+52)' },
    { code: '+55', label: '🇧🇷 Brazil (+55)' },
    { code: '+27', label: '🇿🇦 South Africa (+27)' },
    { code: '+82', label: '🇰🇷 South Korea (+82)' },
    { code: '+62', label: '🇮🇩 Indonesia (+62)' },
    { code: '+66', label: '🇹🇭 Thailand (+66)' },
    { code: '+63', label: '🇵🇭 Philippines (+63)' },
    { code: '+60', label: '🇲🇾 Malaysia (+60)' },
    { code: '+64', label: '🇳🇿 New Zealand (+64)' },
    { code: '+20', label: '🇪🇬 Egypt (+20)' },
    { code: '+972', label: '🇮🇱 Israel (+972)' },
    { code: '+380', label: '🇺🇦 Ukraine (+380)' }        
    // ...add more as needed for your audience
  ];

  return (
    <div className="bg-earth-800/50 p-6 rounded-lg">
      <h2 className="text-2xl font-display mb-6 font-bold text-sand-100">Get Verified <span className="ml-2 text-sand-400 text-base font-normal">(Coming Soon)</span></h2>
      <form /* onSubmit={handleSubmit(onSubmit)} */ className="space-y-6" onSubmit={e => { e.preventDefault(); }}>
        <div>
          <label className="block text-sm font-medium text-sand-300 mb-2">Full Name</label>
          <input
            {...register('full_name')}
            className="w-full px-3 py-2 bg-earth-800 border border-earth-700 text-sand-100 rounded-lg focus:outline-none focus:ring-1 focus:ring-sand-400"
          />
          {errors.full_name && <p className="mt-1 text-sm text-red-500">{errors.full_name.message}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-sand-300 mb-2">Bio</label>
          <textarea
            {...register('bio')}
            rows={4}
            className="w-full px-3 py-2 bg-earth-800 border border-earth-700 text-sand-100 rounded-lg focus:outline-none focus:ring-1 focus:ring-sand-400"
            placeholder="Tell us about yourself..."
          />
          {errors.bio && <p className="mt-1 text-sm text-red-500">{errors.bio.message}</p>}
        </div>
        {/* Instagram Connect/Preview Section */}
        <div className="flex justify-center">
          {!user.instagram ? (
            <div className="bg-earth-900/80 rounded-lg p-3 flex flex-col gap-2 shadow-lg border border-sand-800 mt-2 w-full max-w-xs mx-auto">
              <span className="text-sand-200 font-semibold">Connect Instagram</span>
              <span className="text-sand-400 text-xs mb-2">Connect Instagram to see friends, share your experiences, and unlock social features.</span>
              <div className="flex justify-center">
                <Button
                  size="sm"
                  variant="primary"
                  onClick={handleConnectInstagram}
                  disabled={connectingInstagram}
                  className="w-full"
                >
                  {connectingInstagram ? 'Connecting...' : 'Connect Instagram'}
                </Button>
              </div>
            </div>
          ) : (
            <a
              href={`https://instagram.com/${user.instagram}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-3 py-1 rounded-full bg-gradient-to-r from-pink-500 via-red-500 to-yellow-500 text-white font-semibold shadow hover:opacity-90 transition mt-2 justify-center mx-auto"
              style={{ maxWidth: 'fit-content' }}
            >
              <Instagram className="h-5 w-5" />
              @{user.instagram}
            </a>
          )}
        </div>
        {/* Invite Friends button below Instagram connect */}
        <div className="flex justify-center mt-4">
          <Button
            variant="secondary"
            size="md"
            onClick={() => window.dispatchEvent(new CustomEvent('open-invite-modal'))}
            className="rounded-full px-6 py-2 font-semibold shadow-md w-full max-w-xs"
          >
            Invite Friends
          </Button>
        </div>
        {/* Referrals for verification */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
          <div>
            <label className="block text-sm font-medium text-sand-300 mb-2">Referral 1 (email or username)</label>
            <input
              type="text"
              value={referral1}
              onChange={e => setReferral1(e.target.value)}
              className="w-full px-3 py-2 bg-earth-800 border border-earth-700 text-sand-100 rounded-lg focus:outline-none focus:ring-1 focus:ring-sand-400"
              placeholder="Enter referral..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-sand-300 mb-2">Referral 2 (email or username)</label>
            <input
              type="text"
              value={referral2}
              onChange={e => setReferral2(e.target.value)}
              className="w-full px-3 py-2 bg-earth-800 border border-earth-700 text-sand-100 rounded-lg focus:outline-none focus:ring-1 focus:ring-sand-400"
              placeholder="Enter referral..."
            />
          </div>
        </div>
        {/* Phone number with country code toggle */}
        <div>
          <label className="block text-sm font-medium text-sand-300 mb-2">Phone Number</label>
          <div className="flex flex-col gap-2 sm:flex-row">
            <select
              value={countryCode}
              onChange={e => setCountryCode(e.target.value)}
              className="px-2 py-2 bg-earth-800 border border-earth-700 text-sand-100 rounded-lg focus:outline-none focus:ring-1 focus:ring-sand-400 w-full sm:w-auto"
            >
              {countryCodes.map(opt => (
                <option key={opt.code} value={opt.code}>{opt.label}</option>
              ))}
            </select>
            <input
              type="tel"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              className="px-3 py-2 bg-earth-800 border border-earth-700 text-sand-100 rounded-lg focus:outline-none focus:ring-1 focus:ring-sand-400 w-full"
              placeholder="Phone number"
            />
          </div>
        </div>
        {error && (
          <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
            <p className="text-sm text-red-500">{error}</p>
          </div>
        )}
        {success && (
          <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
            <p className="text-sm text-green-500">Profile updated successfully!</p>
          </div>
        )}
        <div className="flex justify-center mt-6">
          <Button
            type="submit"
            disabled
            className="rounded-full px-6 py-2 font-semibold cursor-not-allowed opacity-60 w-full max-w-xs"
            title="Profile verification is coming soon!"
          >
            Coming Soon
          </Button>
        </div>
      </form>
    </div>
  );
}